/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injector, signal } from '@angular/core';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { assets } from '../../core/constants/assets.const';
import { CheckboxModule } from 'primeng/checkbox';
import { IntlTelInputDirective } from '../../core/directive/IntlTelInputDirective';
import { forkJoin } from 'rxjs';
import { BUSINESS_ROLES, LIST_TYPE } from '../../core/constants/keys.const';
import { BaseClass } from '../../shared/class/BaseClass';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DocumentService } from '../../service/documents.service';
import { AddMemberPayload } from '../../models/BusinessMember.model';
import { BusinessMemberService } from '../../service/businessMember.service';
import { appNoSpaceAtFirst } from '../../core/directive/no-space.directive';
interface Country {
  name: string;
  code: string;
}

export interface Option {
  _id: string;
  name: string;
}
@Component({
    selector: 'notchmark-add-new-member',
    imports: [ButtonModule, RouterModule, Select, InputTextModule, FormsModule, CheckboxModule,IntlTelInputDirective, ReactiveFormsModule, appNoSpaceAtFirst],
    templateUrl: './add-new-member.component.html',
    styleUrl: './add-new-member.component.scss'
})
export class AddNewMemberComponent extends BaseClass implements OnInit {
  @ViewChild(IntlTelInputDirective) telInputDirective!: IntlTelInputDirective;
 
  assetsConst = assets;
  memberForm!: FormGroup;
  value1: string | undefined;
  cities: Country[] | undefined;
  selectedCity: Country | undefined;
  role: Country[] | undefined;
  selectedRole: Country | undefined;
  documents = signal<Option[]>([]);
  checked = false;
  isEdit = false;
  memberId: string | null = null;

  private DocumentService = inject(DocumentService);
  private businessMemberService = inject(BusinessMemberService);
  private ref = inject(DynamicDialogRef);
  public config = inject(DynamicDialogConfig);

  // eslint-disable-next-line @angular-eslint/prefer-inject
  constructor(injector: Injector) {
    super(injector);
  }

  ngOnInit() {
    this.isEdit = this.config.data?.edit ?? false;
    this.memberId = this.config.data?.memberId ?? null;

    this.memberForm = this.formBuilder.group({
      fullName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      countryCode: [''],
      qualification: [null, Validators.required],
      role: [null, Validators.required]
    });

this.role = [
  { name: 'Business Admin', code: String(BUSINESS_ROLES.BUSINESS_ADMIN) },
  { name: 'Business Member', code: String(BUSINESS_ROLES.BUSINESS_MEMBER) }
];


    this.loadListsAndProfile();
    if (this.isEdit && this.memberId) {
      this.loadMemberData(this.memberId);
    }
  }

  phoneChange(event: { isoCode: string; dialCode: string }): void {
    const control = this.memberForm.get('countryCode');
    if (control) {
      control.setValue(event.dialCode);
    }
  }

  private loadListsAndProfile(): void {
    forkJoin({
      documentTypes: this._authService.getSettingsData(
        `type=${LIST_TYPE.DEPARTMENTS}`
      ),
      profile: this._authService.getProfile(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: (result) => {
          this.documents.set(this.toOptions(result.documentTypes));
        },
      });
  }

  private loadMemberData(memberId: string): void {
    this.businessMemberService
      .getMemberById(memberId)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: (member) => {
          this.memberForm.patchValue({
            fullName: member.fullName,
            email: member.email,
            phone: member.phone && member.countryCode ? `${member.countryCode}${member.phone}` : member.phone,
            countryCode: member.countryCode,
            qualification: this.documents().find(d => d._id === member.departmentId),
            role: this.role?.find(r => r.code === String(member.role))
          });
        },
      });
  }

  private toOptions(arr: any[]): Option[] {
    return (arr ?? []).map((x) => ({
      _id: x._id ?? x.code ?? x.value,
      name: x.name ?? x.label ?? String(x),
    }));
  }

  closeDialog() {
    this.ref.close(false);
  }

  onSubmit() {
    if (this.memberForm.valid) {
      const formValue = this.memberForm.value;
      
      const payload: AddMemberPayload = {
        role: Number(formValue.role.code),
        fullName: formValue.fullName,
        email: formValue.email,
        countryCode: formValue.countryCode || '+91',
        phone: formValue.phone,
        departmentId: formValue.qualification._id
      };

      if (this.isEdit && this.memberId) {
        // Update existing member
        this.businessMemberService
          .updateMember(this.memberId, payload)
          .pipe(takeUntilDestroyed(this.destroyRef$))
          .subscribe({
            next: (response) => {
              this.ref.close({ success: true, data: response, action: 'update' });
            },

          });
      } else {

        this.businessMemberService
          .addMember(payload)
          .pipe(takeUntilDestroyed(this.destroyRef$))
          .subscribe({
            next: (response) => {
              this.ref.close({ success: true, data: response, action: 'add' });
            },

          });
      }
    } else {
      this.memberForm.markAllAsTouched();
    }
  }
}
